/**
 * SecureStorage — Encrypts sensitive values in localStorage using AES-GCM.
 *
 * Derives an encryption key from the user's account ID + a salt using PBKDF2.
 * When the user is authenticated, tokens are stored encrypted. When not
 * authenticated, falls back to plaintext (preserving current behavior for
 * anonymous users).
 *
 * This is defense-in-depth — if an XSS attack reads localStorage, they get
 * ciphertext instead of raw API keys. The encryption key is derived from
 * session state that the attacker would need to separately exfiltrate.
 */

const SALT = 'tileforge-secure-v1';
const IV_LENGTH = 12;

let _cachedKey = null;
let _cachedUserId = null;

/**
 * Derive an AES-GCM key from a user ID.
 * @param {string} userId
 * @returns {Promise<CryptoKey>}
 */
async function deriveKey(userId) {
  if (_cachedKey && _cachedUserId === userId) return _cachedKey;

  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(userId + SALT),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  _cachedKey = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(SALT), iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
  _cachedUserId = userId;
  return _cachedKey;
}

/**
 * Encrypt a string value.
 * @returns {string} Base64-encoded IV + ciphertext, prefixed with "enc:"
 */
async function encrypt(value, userId) {
  const key = await deriveKey(userId);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const enc = new TextEncoder();
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(value),
  );

  // Combine IV + ciphertext into one buffer
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return 'enc:' + btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt a previously encrypted value.
 * @param {string} stored — The "enc:..." string from localStorage
 * @returns {string|null} Decrypted value, or null if decryption fails
 */
async function decrypt(stored, userId) {
  if (!stored.startsWith('enc:')) return null;

  try {
    const key = await deriveKey(userId);
    const binary = atob(stored.slice(4));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    const iv = bytes.slice(0, IV_LENGTH);
    const ciphertext = bytes.slice(IV_LENGTH);

    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext,
    );

    return new TextDecoder().decode(plaintext);
  } catch {
    return null; // Decryption failed — wrong key, corrupted, etc.
  }
}

/** Check if Web Crypto is available. */
function isSupported() {
  return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
}

export class SecureStorage {
  /**
   * @param {function} getUserId — Returns current user ID or null
   */
  constructor(getUserId) {
    this._getUserId = getUserId;
  }

  /**
   * Store a value, encrypted if user is authenticated.
   * @param {string} key — localStorage key
   * @param {string} value — plaintext value to store
   */
  async set(key, value) {
    if (!value) {
      localStorage.removeItem(key);
      return;
    }

    const userId = this._getUserId();
    if (userId && isSupported()) {
      try {
        const encrypted = await encrypt(value, userId);
        localStorage.setItem(key, encrypted);
        return;
      } catch (err) {
        console.warn('[SecureStorage] Encryption failed, storing plaintext:', err);
      }
    }

    // Fallback: plaintext
    localStorage.setItem(key, value);
  }

  /**
   * Retrieve a value, decrypting if needed.
   * @param {string} key — localStorage key
   * @returns {Promise<string|null>}
   */
  async get(key) {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    // Encrypted value
    if (stored.startsWith('enc:')) {
      const userId = this._getUserId();
      if (!userId || !isSupported()) return null; // Can't decrypt without user
      const decrypted = await decrypt(stored, userId);
      return decrypted;
    }

    // Plaintext (legacy or anonymous)
    return stored;
  }

  /**
   * Remove a stored value.
   * @param {string} key
   */
  remove(key) {
    localStorage.removeItem(key);
  }

  /**
   * Re-encrypt all managed keys with the current user's key.
   * Call this after sign-in to upgrade plaintext tokens to encrypted.
   * @param {string[]} keys — localStorage keys to re-encrypt
   */
  async reEncrypt(keys) {
    const userId = this._getUserId();
    if (!userId || !isSupported()) return;

    for (const key of keys) {
      const stored = localStorage.getItem(key);
      if (stored && !stored.startsWith('enc:')) {
        try {
          const encrypted = await encrypt(stored, userId);
          localStorage.setItem(key, encrypted);
        } catch { /* leave as-is */ }
      }
    }
  }

  /** Clear the cached encryption key (call on sign-out). */
  clearKeyCache() {
    _cachedKey = null;
    _cachedUserId = null;
  }
}
